// In-memory only -- survives New Game not touching it, but not a reload, unless
// restored from a save (see gameSave.ts/ui.ts's loadGame()).
import { getWorld } from './activeWorld';


// The catalogue itself moved to the sim (2026-08-13), so that "is this key
// consumed when used" -- the one mechanical fact in it -- is answerable below
// the renderer. Re-exported unchanged so every call site here keeps working:
// the thin same-named delegator pattern from design/14_Refactoring_Method.md.
import { ITEMS, ItemDef } from '../../rune-lab/src/sim/items';
export type { ItemDef };

// The set itself lives in the shared WorldState as of 2026-07-30 (sim
// extraction step 2); this module is the accessor layer over it plus the ITEMS
// catalog above, with an unchanged public API.
function held(): Set<string> {
  return getWorld().inventory;
}

// Append-only, never cleared by removeItem -- see WorldState.collectedItemIds'
// own doc comment for the bug this exists to close (a one-use key's crate
// reading as never-opened again, and handing out a fresh key, the moment the
// original was consumed unlocking its door).
function collected(): Set<string> {
  return getWorld().collectedItemIds;
}

export function hasItem(id: string): boolean {
  return held().has(id);
}

// Whether this item has ever been granted, regardless of whether it's still
// held -- what a one-time `giveItemId` grant should actually gate on, not
// `hasItem` (which goes back to false the moment a one-use key is consumed).
export function hasEverCollected(id: string): boolean {
  return collected().has(id);
}

export function addItem(id: string): void {
  held().add(id);
  collected().add(id);
}

export function removeItem(id: string): void {
  held().delete(id);
}

export function getItemDef(id: string): ItemDef | undefined {
  return ITEMS[id];
}

export function getInventoryItems(): ItemDef[] {
  return [...held()].map((id) => ITEMS[id]).filter((item): item is ItemDef => !!item);
}

export function resetInventory(): void {
  held().clear();
  collected().clear();
}

// Raw ids (not ItemDef) for gameSave.ts to serialize/restore wholesale.
export function getHeldItemIds(): string[] {
  return [...held()];
}

export function setHeldItemIds(ids: string[]): void {
  const h = held();
  h.clear();
  for (const id of ids) h.add(id);
}

// Same shape as getHeldItemIds/setHeldItemIds, for collectedItemIds.
export function getCollectedItemIds(): string[] {
  return [...collected()];
}

export function setCollectedItemIds(ids: string[]): void {
  const c = collected();
  c.clear();
  for (const id of ids) c.add(id);
}
